import { FormControl } from '@chakra-ui/react';
import { useClassFiltersStore } from '@stores/classes';
import { useListLocations } from 'hooks/useApiQueries';
import { useCoachStudioParams } from 'hooks/useCoachStudioParams';
import { useLayoutEffect, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { Select } from 'chakra-react-select';
import { ChakraStylesConfig } from 'chakra-react-select';

interface StudioOption {
  label: string;
  value: number;
}

export function ClassesFiltersStudios() {
  const { t } = useTranslation();
  const { data: studios } = useListLocations();
  const studioIds = useClassFiltersStore((state) => state.studioIds);
  const setStudioIds = useClassFiltersStore((state) => state.setStudioIds);
  const { isStudioUrl, id } = useCoachStudioParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Mettre à jour le studioId lorsque l'URL contient un studio
  useLayoutEffect(() => {
    if (isStudioUrl) setStudioIds([id]);
  }, [isStudioUrl, id, setStudioIds]);

  // Observer les changements dans l'URL
  useEffect(() => {
    if (isStudioUrl) {
      const studioPath = `/studio/${id}`;
      const hashIndex = location.pathname.indexOf('#');
      const pathWithoutHash = hashIndex !== -1 ? location.pathname.slice(0, hashIndex) : location.pathname;

      if (pathWithoutHash.includes(studioPath)) {
        const newPath = pathWithoutHash.replace(studioPath, '');
        navigate(newPath, { replace: true });
      }
    }
  }, [location.pathname, isStudioUrl, id, navigate]);

  const options: StudioOption[] = Array.isArray(studios)
    ? [
        ...studios
          .filter((studio) => {
            if (!mondayMindbody?.siteId) return true;
            return mondayMindbody.siteId === String(studio.SiteID);
          })
          .map((studio) => ({
            label: studio.Name.replace(/^(Dynamo|Risse|Punch)\s*/i, 'Studio '),
            value: studio.Id,
          })),
      ]
    : [];

  const values = options.filter((option) => studioIds.includes(option.value));

  const chakraStyles: ChakraStylesConfig<StudioOption, true> = {
    container: (provided) => ({
      ...provided,
      minWidth: '200px',
    }),
    control: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderColor: 'gray.200',
      _hover: {
        borderColor: 'primary.500',
      },
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? 'primary.500' : 'white',
      color: state.isSelected ? 'white' : 'gray.700',
      _hover: {
        backgroundColor: state.isSelected ? 'primary.600' : 'gray.100',
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      backgroundColor: 'primary.100',
      fontSize: '16px',
      padding: '5px',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: 'primary.700',
    }),
  };

  return (
    <FormControl>
      <Select<StudioOption, true>
        size="sm"
        className="studioSelect"
        isMulti
        name="studios"
        options={options}
        placeholder={t('Sélectionner un ou plusieurs studios')}
        closeMenuOnSelect={false}
        value={values}
        onChange={(options) => {
          const selectedIds = options ? options.map((option) => option.value) : [];
          setStudioIds(selectedIds);
        }}
        chakraStyles={chakraStyles}
      />
    </FormControl>
  );
}
